import React, {createContext, useContext, useEffect, useRef, useState} from 'react';
import {auth, firestore} from '../config/firebase';
import {FirebaseAuthTypes} from '@react-native-firebase/auth';
import AsyncStorage from '@react-native-async-storage/async-storage';
import firestoreModule from '@react-native-firebase/firestore';
import {refreshFCMToken, clearFCMToken} from '../services/notifications';

interface Account {
  id: string;
  name: string;
  displayName?: string; // Short name for map tabs (max 15 chars)
  municipality: string;
  adminIds: string[];
  userIds: string[];
  phoneNumbers: string[];
  mutualAidCode?: string;
  mutualAidPartners?: string[];
  enabledMutualAidPartners?: string[];
  invitationCode?: string;
  invitationCodeExpiresAt?: Date;
  publicVisibility?: boolean; // For civilian access (default: true)
  civilianMapAccess?: boolean; // Whether existing civilian followers can view closures (default: true)
  practiceMode?: boolean; // Training mode: shows a banner that closures may not be live (default: false)
  publicMapEnabled?: boolean; // Whether the public shareable map link is active (default: false)
  verified?: boolean; // Whether admin email has been verified (default: false)
  location?: {latitude: number; longitude: number}; // For GPS-based search
  createdAt: Date;
  graceStartDate?: Date; // 30-day grace period for municipalities before subscription required
  // Subscription fields
  subscription?: {
    status: 'active' | 'canceled' | 'past_due' | 'trialing' | 'incomplete' | 'incomplete_expired' | 'unpaid';
    subscriptionId: string; // Stripe subscription ID
    priceId: string; // Stripe price ID
    currentPeriodStart: Date;
    currentPeriodEnd: Date;
    cancelAtPeriodEnd?: boolean;
    canceledAt?: Date;
    trialEnd?: Date;
  };
  subscriptionTier?: 1 | 2 | 3 | 4; // Current tier based on user count
  billingEmail?: string; // Email for billing notifications
}

interface UserProfile {
  id: string;
  email: string;
  name: string;
  accountId: string; // Empty for civilians
  role: 'admin' | 'user' | 'civilian';
  phoneNumber?: string;
  followedAccounts?: string[]; // For civilians: array of accountIds they follow
  notificationPreferences?: {[accountId: string]: boolean}; // For civilians: per-account notification toggles
  mutualAidNotificationPreferences?: {[partnerId: string]: boolean}; // For municipality users: per-partner notification toggles
  onDuty?: boolean; // For municipality users: whether they receive notifications (default true)
  trialStartDate?: Date; // For civilians: when their 30-day free trial started
  closedSoundFilename?: string; // Sound filename for closed notifications (for Cloud Function)
  openedSoundFilename?: string; // Sound filename for opened notifications (for Cloud Function)
  subscription?: {
    status: 'active' | 'canceled' | 'past_due' | 'expired' | 'trialing' | 'incomplete' | 'incomplete_expired' | 'unpaid';
    source?: 'stripe' | 'apple';
    stripeSubscriptionId?: string;
    stripeCustomerId?: string;
    appleOriginalTransactionId?: string;
    appleProductId?: string;
    currentPeriodStart?: Date | any;
    currentPeriodEnd?: Date | any;
    cancelAtPeriodEnd?: boolean;
    quantity?: number;
  };
  createdAt: Date;
}

interface AuthContextType {
  user: FirebaseAuthTypes.User | null;
  userProfile: UserProfile | null;
  account: Account | null;
  loading: boolean;
  userLocation: {latitude: number; longitude: number} | null;
  setUserLocation: (location: {latitude: number; longitude: number} | null) => void;
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string, name: string, municipality: string, userType: 'admin' | 'join' | 'civilian', invitationCode?: string, displayName?: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshAccount: () => Promise<void>;
  refreshUserProfile: () => Promise<void>;
  createAccountForUser: (municipality: string) => Promise<string>;
  linkAccountWithInvitationCode: (invitationCode: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{children: React.ReactNode}> = ({children}) => {
  console.log('🟣 AuthProvider: Component rendering...');
  const [user, setUser] = useState<FirebaseAuthTypes.User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [account, setAccount] = useState<Account | null>(null);
  const [loading, setLoading] = useState(true);
  const [userLocation, setUserLocation] = useState<{latitude: number; longitude: number} | null>(null);
  const lastAuthUidRef = useRef<string | null>(null);
  // Holds the live account-document listener unsubscribe fn so we can tear it
  // down on logout / account switch / unmount.
  const accountUnsubscribeRef = useRef<(() => void) | null>(null);
  console.log('🟣 AuthProvider: State initialized, loading =', loading);

  const fetchUserProfile = async (uid: string, retryCount = 0) => {
    try {
      const profileDoc = await firestore().collection('users').doc(uid).get();
      if (profileDoc.exists) {
        const profileData = profileDoc.data();
        
        // Handle case where document exists but data is not yet available (race condition during signup)
        if (!profileData || Object.keys(profileData).length === 0) {
          if (retryCount < 5) {
            console.log(`User profile document exists but data is empty, retrying... (attempt ${retryCount + 1}/5)`);
            await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second
            return fetchUserProfile(uid, retryCount + 1);
          } else {
            console.error('❌ User profile document exists but has no data after retries');
            return;
          }
        }
        
        const role = (profileData?.role || 'user') as 'admin' | 'user' | 'civilian';
        const accountId = profileData?.accountId || '';
        
        console.log('User profile loaded:', {
          uid,
          email: profileData?.email,
          name: profileData?.name,
          accountId: accountId || 'MISSING',
          role: role,
        });
        
        // For civilian users, empty accountId is expected (they don't belong to a municipality)
        // Only retry for non-civilian users who are missing accountId
        if (!accountId && role !== 'civilian' && retryCount < 5) {
          console.log(`User profile exists but accountId is missing, retrying... (attempt ${retryCount + 1}/5)`);
          await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second
          return fetchUserProfile(uid, retryCount + 1);
        }
        
        const profile: UserProfile = {
          id: uid,
          email: profileData?.email || '',
          name: profileData?.name || '',
          accountId: accountId,
          role: role,
          phoneNumber: profileData?.phoneNumber,
          followedAccounts: profileData?.followedAccounts || [],
          notificationPreferences: profileData?.notificationPreferences || {},
          mutualAidNotificationPreferences: profileData?.mutualAidNotificationPreferences || {},
          onDuty: profileData?.onDuty, // Read onDuty status from Firestore
          trialStartDate: profileData?.trialStartDate?.toDate(),
          subscription: profileData?.subscription ? {
            status: profileData.subscription.status,
            source: profileData.subscription.source || (profileData.subscription.stripeSubscriptionId ? 'stripe' : undefined),
            stripeSubscriptionId: profileData.subscription.stripeSubscriptionId,
            stripeCustomerId: profileData.subscription.stripeCustomerId,
            appleOriginalTransactionId: profileData.subscription.appleOriginalTransactionId,
            appleProductId: profileData.subscription.appleProductId,
            currentPeriodStart: profileData.subscription.currentPeriodStart?.toDate(),
            currentPeriodEnd: profileData.subscription.currentPeriodEnd?.toDate(),
            cancelAtPeriodEnd: profileData.subscription.cancelAtPeriodEnd,
          } : undefined,
          createdAt: profileData?.createdAt?.toDate() || new Date(),
        };
        setUserProfile(profile);
        
        if (accountId) {
          await fetchAccount(accountId);
        } else if (role !== 'civilian') {
          // Only show error for non-civilian users missing accountId
          console.error('❌ User profile missing accountId after retries. User may need to be linked to an account.');
          console.error('Profile data:', profileData);
          // Don't set account to null - let the UI show the error
        } else {
          // Civilian users don't have an accountId - this is expected
          console.log('✅ Civilian user profile loaded (no accountId expected)');
        }
      } else {
        // If profile doesn't exist yet (e.g., during signup), retry a few times
        if (retryCount < 5) {
          console.log(`User profile not found, retrying... (attempt ${retryCount + 1}/5)`);
          await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second
          return fetchUserProfile(uid, retryCount + 1);
        } else {
          console.error('❌ User profile document does not exist for uid:', uid);
        }
      }
    } catch (error: any) {
      console.error('❌ Error fetching user profile:', error);
      console.error('Error details:', {
        code: error?.code,
        message: error?.message,
        uid,
      });
    }
  };

  // Map a raw account document into our typed Account shape.
  const buildAccountInfo = (accountId: string, accountData: any): Account => {
    const subscriptionData = accountData?.subscription;
    return {
      id: accountId,
      name: accountData?.name || '',
      displayName: accountData?.displayName,
      municipality: accountData?.municipality || '',
      adminIds: accountData?.adminIds || [],
      userIds: accountData?.userIds || [],
      phoneNumbers: accountData?.phoneNumbers || [],
      mutualAidCode: accountData?.mutualAidCode,
      mutualAidPartners: accountData?.mutualAidPartners || [],
      enabledMutualAidPartners: accountData?.enabledMutualAidPartners || [],
      invitationCode: accountData?.invitationCode,
      invitationCodeExpiresAt: accountData?.invitationCodeExpiresAt?.toDate(),
      publicVisibility: accountData?.publicVisibility !== undefined ? accountData.publicVisibility : true,
      civilianMapAccess: accountData?.civilianMapAccess !== undefined ? accountData.civilianMapAccess : true,
      practiceMode: accountData?.practiceMode === true,
      publicMapEnabled: accountData?.publicMapEnabled === true,
      verified: accountData?.verified === true,
      location: accountData?.location ? {
        latitude: accountData.location.latitude,
        longitude: accountData.location.longitude,
      } : undefined,
      createdAt: accountData?.createdAt?.toDate() || new Date(),
      graceStartDate: accountData?.graceStartDate?.toDate() || accountData?.createdAt?.toDate(),
      subscription: subscriptionData ? {
        status: subscriptionData.status,
        subscriptionId: subscriptionData.subscriptionId,
        priceId: subscriptionData.priceId,
        currentPeriodStart: subscriptionData.currentPeriodStart?.toDate() || new Date(),
        currentPeriodEnd: subscriptionData.currentPeriodEnd?.toDate() || new Date(),
        cancelAtPeriodEnd: subscriptionData.cancelAtPeriodEnd,
        canceledAt: subscriptionData.canceledAt?.toDate(),
        trialEnd: subscriptionData.trialEnd?.toDate(),
      } : undefined,
      subscriptionTier: accountData?.subscriptionTier,
      billingEmail: accountData?.billingEmail,
    };
  };

  // Auto-sync: if admin's Firebase Auth email is verified but account isn't, update Firestore.
  const maybeAutoVerifyAccount = (accountId: string, accountInfo: Account) => {
    const currentUser = auth().currentUser;
    if (
      currentUser &&
      currentUser.emailVerified &&
      !accountInfo.verified &&
      accountInfo.adminIds.includes(currentUser.uid)
    ) {
      console.log('✅ Admin email verified — auto-updating account verified status');
      firestore()
        .collection('accounts')
        .doc(accountId)
        .update({verified: true})
        .then(() => {
          setAccount(prev => prev ? {...prev, verified: true} : prev);
        })
        .catch(err => console.warn('Failed to auto-verify account:', err));
    }
  };

  // Subscribe to the account document in real time so changes made on any device
  // (e.g. an admin toggling Practice Mode on the web) propagate live without an
  // app restart. Returns a promise that resolves on the first snapshot so callers
  // that `await fetchAccount(...)` still have account state populated before continuing.
  const fetchAccount = async (accountId: string): Promise<void> => {
    if (!accountId) {
      console.warn('fetchAccount called with empty accountId');
      return;
    }

    // Tear down any previous account listener (e.g. switching accounts).
    if (accountUnsubscribeRef.current) {
      accountUnsubscribeRef.current();
      accountUnsubscribeRef.current = null;
    }

    console.log('Subscribing to account:', accountId);
    return new Promise<void>((resolve) => {
      let settled = false;
      const settleOnce = () => {
        if (!settled) {
          settled = true;
          resolve();
        }
      };

      accountUnsubscribeRef.current = firestore()
        .collection('accounts')
        .doc(accountId)
        .onSnapshot(
          (accountDoc) => {
            if (accountDoc.exists) {
              const accountData = accountDoc.data();
              const accountInfo = buildAccountInfo(accountId, accountData);
              setAccount(accountInfo);
              maybeAutoVerifyAccount(accountId, accountInfo);
            } else {
              console.error('Account document does not exist:', accountId);
            }
            settleOnce();
          },
          (error: any) => {
            console.error('Error in account listener:', error);
            console.error('Error details:', {
              code: error?.code,
              message: error?.message,
              accountId,
            });
            settleOnce();
          },
        );
    });
  };

  useEffect(() => {
    console.log('🔵 AuthContext: Setting up auth listener...');
    
    // Fallback timeout - if auth doesn't respond in 5 seconds, stop loading anyway
    const timeoutId = setTimeout(() => {
      console.log('⚠️ AuthContext: Auth timeout - forcing loading to false');
      setLoading(false);
    }, 5000);
    
    const unsubscribe = auth().onAuthStateChanged(async (currentUser) => {
      console.log('🔵 AuthContext: Auth state changed, user:', currentUser?.uid || 'null');
      clearTimeout(timeoutId); // Cancel the timeout since auth responded

      // Prevent stale cross-account/session map centering from persisted in-memory location.
      if (lastAuthUidRef.current !== currentUser?.uid) {
        setUserLocation(null);
        await AsyncStorage.removeItem('pendingClosureLocation');
      }
      lastAuthUidRef.current = currentUser?.uid || null;

      setUser(currentUser);
      if (currentUser) {
        console.log('🔵 AuthContext: Fetching user profile...');
        await fetchUserProfile(currentUser.uid);
        await AsyncStorage.setItem('userId', currentUser.uid);
        // Refresh FCM token after user is authenticated
        setTimeout(async () => {
          try {
            console.log('🔔 Refreshing FCM token after authentication...');
            await refreshFCMToken();
            console.log('✅ FCM token refresh completed');
          } catch (error) {
            console.error('❌ Error refreshing FCM token after auth:', error);
            // Don't throw - allow app to continue even if token refresh fails
          }
        }, 500);
      } else {
        console.log('🔵 AuthContext: No user, clearing profile...');
        // Tear down the live account listener on logout.
        if (accountUnsubscribeRef.current) {
          accountUnsubscribeRef.current();
          accountUnsubscribeRef.current = null;
        }
        setUserProfile(null);
        setAccount(null);
        setUserLocation(null);
        await AsyncStorage.removeItem('userId');
        await AsyncStorage.removeItem('pendingClosureLocation');
      }
      console.log('🔵 AuthContext: Setting loading to false');
      setLoading(false);
    });

    return () => {
      clearTimeout(timeoutId);
      unsubscribe();
      // Tear down the live account listener on unmount.
      if (accountUnsubscribeRef.current) {
        accountUnsubscribeRef.current();
        accountUnsubscribeRef.current = null;
      }
    };
  }, []);

  const login = async (email: string, password: string) => {
    try {
      // Trim whitespace from email and password
      const trimmedEmail = email.trim();
      const trimmedPassword = password.trim();
      
      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(trimmedEmail)) {
        throw new Error('Invalid email address. Please check your email.');
      }
      
      // Validate password is not empty
      if (!trimmedPassword) {
        throw new Error('Password cannot be empty.');
      }
      
      console.log('🔐 Attempting login for:', trimmedEmail);
      await auth().signInWithEmailAndPassword(trimmedEmail, trimmedPassword);
      console.log('✅ Login successful');
    } catch (error: any) {
      console.error('❌ Login error:', error);
      console.error('Error code:', error?.code);
      console.error('Error message:', error?.message);
      
      // Provide user-friendly error messages
      let errorMessage = 'Login failed';
      if (error?.code === 'auth/user-not-found') {
        errorMessage = 'No account found with this email. Please sign up first.';
      } else if (error?.code === 'auth/wrong-password' || error?.code === 'auth/invalid-credential') {
        errorMessage = 'Incorrect email or password. Please check your credentials and try again.';
      } else if (error?.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address. Please check your email.';
      } else if (error?.code === 'auth/user-disabled') {
        errorMessage = 'This account has been disabled. Please contact support.';
      } else if (error?.code === 'auth/too-many-requests') {
        errorMessage = 'Too many failed login attempts. Please try again later.';
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      throw new Error(errorMessage);
    }
  };

  const signup = async (
    email: string,
    password: string,
    name: string,
    municipality: string,
    userType: 'admin' | 'join' | 'civilian',
    invitationCode?: string,
    displayName?: string,
  ) => {
    try {
      console.log('🔐 Starting signup process...', {email, userType, hasInvitationCode: !!invitationCode});
      
      // IMPORTANT: Validate invitation code BEFORE creating Firebase Auth user
      // This prevents orphaned auth accounts if the code is invalid
      let validatedAccount: {accountId: string; name: string; municipality: string} | null = null;
      
      if (invitationCode || userType === 'join') {
        console.log('🔍 Pre-validating invitation code before account creation:', invitationCode);
        const {findAccountByInvitationCode} = await import('../services/invitations');
        validatedAccount = await findAccountByInvitationCode(invitationCode!);

        if (!validatedAccount) {
          console.error('❌ Invalid invitation code:', invitationCode);
          throw new Error('Invalid or expired invitation code. Please check your code and try again.');
        }

        console.log('✅ Invitation code valid, will join account:', validatedAccount.accountId);

        // Also check subscription limit before creating user
        const {canAddUsers} = await import('../services/subscriptions');
        const canAdd = await canAddUsers(validatedAccount.accountId, 1);
        console.log('🔍 canAddUsers result:', JSON.stringify(canAdd));
        if (!canAdd.canAdd) {
          // Show the actual reason from canAddUsers instead of a generic message
          if (canAdd.reason) {
            throw new Error(canAdd.reason);
          }
          // Fallback to generic message only if no reason provided
          const {getSubscriptionInfo, SUBSCRIPTION_TIERS} = await import('../services/subscriptions');
          const currentUserCount = canAdd.currentCount || 0;
          const subscriptionInfo = getSubscriptionInfo(currentUserCount);
          const nextTier = subscriptionInfo.tier < 4 ? (subscriptionInfo.tier + 1) as 1 | 2 | 3 | 4 : 4;
          const nextTierConfig = SUBSCRIPTION_TIERS[nextTier];
          const nextTierMax = nextTierConfig.max === Infinity ? '500+' : nextTierConfig.max.toString();
          
          throw new Error(
            `User limit reached. This account has ${currentUserCount} users (Tier ${subscriptionInfo.tier} allows up to ${subscriptionInfo.maxUsers === 10000 ? '500' : subscriptionInfo.maxUsers} users). ` +
            `The admin needs to upgrade to Tier ${nextTier} (allows up to ${nextTierMax} users) to add more users.`
          );
        }
      }
      
      // Now create the Firebase Auth user (after validation passes)
      const userCredential = await auth().createUserWithEmailAndPassword(email, password);
      const uid = userCredential.user.uid;
      console.log('✅ Firebase Auth user created:', uid);

      const db = firestore();

      // Handle civilian signup (no account creation, just user profile)
      if (userType === 'civilian') {
        console.log('👤 Creating civilian account');
        const userProfileData = {
          email,
          name,
          accountId: '', // Civilians don't have an account
          role: 'civilian',
          followedAccounts: [],
          notificationPreferences: {},
          createdAt: firestoreModule.FieldValue.serverTimestamp(),
          trialStartDate: firestoreModule.FieldValue.serverTimestamp(), // 30-day free trial starts now
        };
        await db.collection('users').doc(uid).set(userProfileData);
        console.log('✅ Civilian profile created successfully with 30-day trial');
        
        // Wait for profile to be written
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Verify profile
        const verifyProfile = await db.collection('users').doc(uid).get();
        if (!verifyProfile.exists) {
          throw new Error('Failed to create user profile. Please try again.');
        }
        return; // Civilian signup complete - they'll search for accounts after
      }

      // If invitation code was validated, join the existing account
      if (validatedAccount) {
        console.log('🔗 Joining validated account:', validatedAccount.accountId);

        // Add user to existing account as a regular user (not admin)
        await db.collection('accounts').doc(validatedAccount.accountId).update({
          userIds: firestoreModule.FieldValue.arrayUnion(uid),
          updatedAt: firestoreModule.FieldValue.serverTimestamp(),
        });
        console.log('✅ User added to account userIds');

        // Update subscription quantity in Stripe if subscription exists
        try {
          // Re-fetch account to get updated user count
          const accountDoc = await db.collection('accounts').doc(validatedAccount.accountId).get();
          const accountData = accountDoc.data();
          if (accountData?.subscription?.stripeSubscriptionId && accountData?.subscription?.status === 'active') {
            const {updateSubscriptionQuantity} = await import('../services/subscriptions');
            // Deduplicate user count - admins may be in both adminIds and userIds
            const allUserIds = [...new Set([...(accountData.adminIds || []), ...(accountData.userIds || [])])];
            const newUserCount = allUserIds.length;
            await updateSubscriptionQuantity(validatedAccount.accountId, newUserCount);
            console.log('✅ Subscription quantity updated in Stripe to:', newUserCount);
          }
        } catch (subscriptionError) {
          // Log but don't fail the signup if subscription update fails
          console.warn('⚠️ Failed to update subscription quantity:', subscriptionError);
        }

        // Create user profile linked to existing account
        const userProfileData = {
          email,
          name,
          accountId: validatedAccount.accountId,
          role: 'user', // New users join as regular users, not admins
          onDuty: true, // Default to on duty for municipality users
          createdAt: firestoreModule.FieldValue.serverTimestamp(),
        };
        console.log('📝 Creating user profile with accountId:', validatedAccount.accountId);
        await db.collection('users').doc(uid).set(userProfileData);
        console.log('✅ User profile created successfully');
        
        // Wait a moment for Firestore to fully write the data
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Verify the profile was created correctly
        const verifyProfile = await db.collection('users').doc(uid).get();
        if (verifyProfile.exists) {
          const profileData = verifyProfile.data();
          console.log('✅ Verified user profile:', {
            accountId: profileData?.accountId,
            email: profileData?.email,
            name: profileData?.name,
            role: profileData?.role,
          });
          if (!profileData?.accountId) {
            console.error('❌ CRITICAL: User profile created but accountId is missing!');
            // Try to fix it by updating the profile again
            console.log('🔄 Attempting to fix missing accountId...');
            await db.collection('users').doc(uid).update({
              accountId: validatedAccount.accountId,
            });
            // Verify again
            const recheckProfile = await db.collection('users').doc(uid).get();
            const recheckData = recheckProfile.data();
            if (!recheckData?.accountId) {
              throw new Error('Failed to link user to account. Please contact support.');
            }
            console.log('✅ Fixed missing accountId');
          }
        } else {
          console.error('❌ CRITICAL: User profile document does not exist after creation!');
          throw new Error('Failed to create user profile. Please try again.');
        }
      } else if (userType === 'admin') {
        // Create new account (admin signup)
        console.log('📝 Creating new account for municipality:', municipality);
        const accountRef = db.collection('accounts').doc();
        const accountData: any = {
          name: municipality,
          municipality,
          adminIds: [uid],
          userIds: [uid],
          phoneNumbers: [],
          publicVisibility: true, // Default to public for civilian access
          civilianMapAccess: true, // Default to sharing map with civilian followers
          verified: false, // Requires email verification to be discoverable by civilians
          createdAt: firestoreModule.FieldValue.serverTimestamp(),
        };
        // Add displayName if provided (optional short name for map tabs)
        if (displayName && displayName.trim()) {
          accountData.displayName = displayName.trim();
        }
        await accountRef.set(accountData);
        console.log('✅ Account created:', accountRef.id);

        // Create user profile
        const userProfileData = {
          email,
          name,
          accountId: accountRef.id,
          role: 'admin',
          onDuty: true, // Default to on duty for municipality users
          createdAt: firestoreModule.FieldValue.serverTimestamp(),
        };
        console.log('📝 Creating user profile with accountId:', accountRef.id);
        await db.collection('users').doc(uid).set(userProfileData);
        console.log('✅ User profile created successfully');
        
        // Wait a moment for Firestore to fully write the data
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Verify the profile was created correctly
        const verifyProfile = await db.collection('users').doc(uid).get();
        if (verifyProfile.exists) {
          const profileData = verifyProfile.data();
          if (!profileData?.accountId) {
            console.error('❌ CRITICAL: User profile created but accountId is missing!');
            // Try to fix it by updating the profile again
            await db.collection('users').doc(uid).update({
              accountId: accountRef.id,
            });
            console.log('✅ Fixed missing accountId');
          }
        }
      }
    } catch (error: any) {
      console.error('❌ Signup error:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        stack: error.stack,
      });
      throw new Error(error.message || 'Signup failed');
    }
  };

  const logout = async () => {
    try {
      await clearFCMToken();
      await auth().signOut();
      await AsyncStorage.removeItem('userId');
      await AsyncStorage.removeItem('pendingClosureLocation');
      setUserLocation(null);
    } catch (error: any) {
      throw new Error(error.message || 'Logout failed');
    }
  };

  const createAccountForUser = async (municipality: string) => {
    if (!user) {
      throw new Error('User not authenticated');
    }
    
    try {
      console.log('Creating new account for user:', user.uid);
      
      // Create account
      const db = firestore();
      const accountRef = db.collection('accounts').doc();
      const accountData = {
        name: municipality,
        municipality,
        adminIds: [user.uid],
        userIds: [user.uid],
        phoneNumbers: [],
        createdAt: firestoreModule.FieldValue.serverTimestamp(),
        graceStartDate: firestoreModule.FieldValue.serverTimestamp(), // 30-day grace period starts now
      };
      await accountRef.set(accountData);
      
      console.log('Account created:', accountRef.id);
      
      // Update user profile with accountId and missing fields
      const userEmail = user.email || '';
      const userDisplayName = user.displayName || municipality;
      
      const userUpdate: any = {
        accountId: accountRef.id,
        role: 'admin',
        onDuty: true, // Default to on duty for municipality users
        createdAt: firestoreModule.FieldValue.serverTimestamp(),
      };
      
      // Only update fields that are missing
      if (!userProfile?.email) {
        userUpdate.email = userEmail;
      }
      if (!userProfile?.name) {
        userUpdate.name = userDisplayName;
      }
      
      await db.collection('users').doc(user.uid).set(userUpdate, {merge: true});
      
      console.log('User profile updated with accountId');
      
      // Reload user profile and account
      await fetchUserProfile(user.uid);
      
      return accountRef.id;
    } catch (error: any) {
      console.error('Error creating account:', error);
      throw error;
    }
  };

  const linkAccountWithInvitationCode = async (invitationCode: string) => {
    if (!user || !userProfile) {
      throw new Error('User not authenticated');
    }

    if (userProfile.accountId) {
      throw new Error('User is already linked to an account');
    }

    try {
      console.log('🔗 Linking account with invitation code:', invitationCode);
      const db = firestore();
      
      // Find account by invitation code
      const {findAccountByInvitationCode} = await import('../services/invitations');
      const account = await findAccountByInvitationCode(invitationCode);

      if (!account) {
        throw new Error('Invalid or expired invitation code');
      }

      console.log('✅ Invitation code valid, linking to account:', account.accountId);

      // Check subscription limit before adding user
      const {canAddUsers} = await import('../services/subscriptions');
      const canAdd = await canAddUsers(account.accountId, 1);
      console.log('🔍 linkAccount canAddUsers result:', JSON.stringify(canAdd));
      if (!canAdd.canAdd) {
        // Show the actual reason from canAddUsers instead of a generic message
        if (canAdd.reason) {
          throw new Error(canAdd.reason);
        }
        // Fallback to generic message only if no reason provided
        const {getSubscriptionInfo, SUBSCRIPTION_TIERS} = await import('../services/subscriptions');
        const currentUserCount = canAdd.currentCount || 0;
        const subscriptionInfo = getSubscriptionInfo(currentUserCount);
        const nextTier = subscriptionInfo.tier < 4 ? (subscriptionInfo.tier + 1) as 1 | 2 | 3 | 4 : 4;
        const nextTierConfig = SUBSCRIPTION_TIERS[nextTier];
        const nextTierMax = nextTierConfig.max === Infinity ? '500+' : nextTierConfig.max.toString();
        
        throw new Error(
          `User limit reached. You have ${currentUserCount} users (Tier ${subscriptionInfo.tier} allows up to ${subscriptionInfo.maxUsers === 10000 ? '500' : subscriptionInfo.maxUsers} users). ` +
          `Upgrade to Tier ${nextTier} (allows up to ${nextTierMax} users) to add more users. ` +
          `Please go to Account settings to upgrade your subscription.`
        );
      }

      // Add user to account's userIds
      await db.collection('accounts').doc(account.accountId).update({
        userIds: firestoreModule.FieldValue.arrayUnion(user.uid),
        updatedAt: firestoreModule.FieldValue.serverTimestamp(),
      });
      console.log('✅ User added to account userIds');

      // Update subscription quantity in Stripe if subscription exists
      try {
        // Re-fetch account to get updated user count
        const accountDoc = await db.collection('accounts').doc(account.accountId).get();
        const accountData = accountDoc.data();
        if (accountData?.subscription?.stripeSubscriptionId && accountData?.subscription?.status === 'active') {
          const {updateSubscriptionQuantity} = await import('../services/subscriptions');
          // Deduplicate user count - admins may be in both adminIds and userIds
          const allUserIds = [...new Set([...(accountData.adminIds || []), ...(accountData.userIds || [])])];
          const newUserCount = allUserIds.length;
          await updateSubscriptionQuantity(account.accountId, newUserCount);
          console.log('✅ Subscription quantity updated in Stripe to:', newUserCount);
        }
      } catch (subscriptionError) {
        // Log but don't fail the linking if subscription update fails
        console.warn('⚠️ Failed to update subscription quantity:', subscriptionError);
      }

      // Update user profile with accountId and missing fields
      const userEmail = user.email || userProfile.email || '';
      const userDisplayName = user.displayName || userProfile.name || '';

      const userUpdate: any = {
        accountId: account.accountId,
        role: 'user', // Users joining via invitation code are regular users
        onDuty: true, // Default to on duty for municipality users
        updatedAt: firestoreModule.FieldValue.serverTimestamp(),
      };

      // Only update fields that are missing
      if (!userProfile.email && userEmail) {
        userUpdate.email = userEmail;
      }
      if (!userProfile.name && userDisplayName) {
        userUpdate.name = userDisplayName;
      }
      if (!userProfile.createdAt) {
        userUpdate.createdAt = firestoreModule.FieldValue.serverTimestamp();
      }

      await db.collection('users').doc(user.uid).set(userUpdate, {merge: true});
      console.log('✅ User profile updated with accountId');

      // Wait a moment for Firestore to write
      await new Promise(resolve => setTimeout(resolve, 500));

      // Reload user profile and account
      await fetchUserProfile(user.uid);
      console.log('✅ Account linked successfully');
    } catch (error: any) {
      console.error('❌ Error linking account:', error);
      throw error;
    }
  };

  const refreshUserProfile = async () => {
    if (!user) {
      console.warn('Cannot refresh user profile: no user');
      return;
    }
    await fetchUserProfile(user.uid);
  };

  const refreshAccount = async () => {
    if (userProfile) {
      if (userProfile.accountId) {
        await fetchAccount(userProfile.accountId);
      } else {
        // Try to find an account that has this user
        console.log('User profile missing accountId, searching for account...');
        try {
          const accountsSnapshot = await firestore()
            .collection('accounts')
            .where('userIds', 'array-contains', userProfile.id)
            .limit(1)
            .get();
          
          if (!accountsSnapshot.empty) {
            const accountDoc = accountsSnapshot.docs[0];
            const accountId = accountDoc.id;
            console.log('Found account for user:', accountId);
            
            // Update user profile with accountId
            await firestore().collection('users').doc(userProfile.id).update({
              accountId: accountId,
            });
            
            // Reload user profile
            await fetchUserProfile(userProfile.id);
          } else {
            // Check if user is in adminIds
            const adminAccountsSnapshot = await firestore()
              .collection('accounts')
              .where('adminIds', 'array-contains', userProfile.id)
              .limit(1)
              .get();
            
            if (!adminAccountsSnapshot.empty) {
              const accountDoc = adminAccountsSnapshot.docs[0];
              const accountId = accountDoc.id;
              console.log('Found account where user is admin:', accountId);
              
              // Update user profile with accountId
              await firestore().collection('users').doc(userProfile.id).update({
                accountId: accountId,
              });
              
              // Reload user profile
              await fetchUserProfile(userProfile.id);
            } else {
              console.error('No account found for user:', userProfile.id);
            }
          }
        } catch (error: any) {
          console.error('Error searching for account:', error);
        }
      }
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        userProfile,
        account,
        loading,
        userLocation,
        setUserLocation,
        login,
        signup,
        logout,
        refreshAccount,
        refreshUserProfile,
        createAccountForUser,
        linkAccountWithInvitationCode,
      }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

